// src/pages/healthcare/consultation/consultationComponents/SuggestionPopup.tsx
// COMPACT VERSION - Maximized information density

import React, { useState, useEffect, useRef, useMemo } from "react";
import { CSSTransition } from "react-transition-group";
import { Card, Button, DatePicker, Tooltip, Input, Tag, Badge, Space, Typography, theme } from "antd";
import { CheckOutlined, DeleteOutlined, DownOutlined, UpOutlined, QuestionCircleOutlined, SearchOutlined, HeartOutlined, CompassOutlined, WarningOutlined, UserAddOutlined, MedicineBoxOutlined, ExperimentOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

// Types
import { Suggestion, SuggestionStatus, SuggestionType } from "../../../../utils/healthcare/SugestionUtils";
import type { UserUuid } from "../../../../types/identity";

const { TextArea } = Input;
const { Text } = Typography;
const { useToken } = theme;

const iconComponents = {
	question: QuestionCircleOutlined,
	investigation: SearchOutlined,
	diagnosis: HeartOutlined,
	treatment: CompassOutlined,
	alert: WarningOutlined,
	referral: UserAddOutlined,
};

const lightColorMapping: { [key in SuggestionType]: string } = {
	question: "#e6f7ff",
	investigation: "#e6fffb",
	diagnosis: "#f9f0ff",
	treatment: "#f9f0ff",
	alert: "#fff1f0",
	referral: "#f6ffed",
};

const darkColorMapping: { [key in SuggestionType]: string } = {
	question: "#0f2940",
	investigation: "#0f2d2c",
	diagnosis: "#2b1d40",
	treatment: "#2b1d40",
	alert: "#401f1f",
	referral: "#1f401f",
};

const urgencySeverity: { [key: string]: "success" | "default" | "warning" | "error" } = {
	low: "success",
	medium: "default",
	high: "warning",
	critical: "error",
};

interface SuggestionPopupProps {
	suggestion: Suggestion;
	patientId: UserUuid;
	onStatusChange: (id: string, newStatus: SuggestionStatus) => void;
	onMappingUpdate?: (field: string, newValue: string) => void;
	onShowMedicalHistoryPreview: (id: string) => void;
	onAddMedication: (id: string) => void;
	onAddTest: (id: string) => void;
	number: number;
}

const SuggestionPopup: React.FC<SuggestionPopupProps> = ({ suggestion, patientId, onStatusChange, onMappingUpdate, onShowMedicalHistoryPreview, onAddMedication, onAddTest, number }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [mappingValues, setMappingValues] = useState<{ [key: string]: string }>(suggestion.mappable_fields || {});
	const [notes, setNotes] = useState<string>("");
	const [animateIn, setAnimateIn] = useState(false);

	const expandContentRef = useRef<HTMLDivElement>(null);
	const { token } = useToken();

	const hasMedicalHistoryRecordType = suggestion.medical_history_record_type !== "";
	const creationPending = (hasMedicalHistoryRecordType || suggestion.medication || suggestion.testing) && !suggestion.suggestionRecordCreated;

	const IconComponent = iconComponents[suggestion.type] || iconComponents.question;

	const isDarkMode = useMemo(() => {
		return document.documentElement.classList.contains("dark-theme");
	}, []);

	const colorMapping = useMemo(() => {
		return isDarkMode ? darkColorMapping : lightColorMapping;
	}, [isDarkMode]);

	useEffect(() => {
		requestAnimationFrame(() => {
			setAnimateIn(true);
		});
		setMappingValues(suggestion.mappable_fields || {});
	}, [suggestion.mappable_fields, suggestion.text]);

	const toggleExpand = () => {
		setIsExpanded((prev) => !prev);
	};

	const handleMappingChange = (field: string, newValue: string) => {
		const updated = { ...mappingValues, [field]: newValue };
		setMappingValues(updated);
		if (onMappingUpdate) {
			onMappingUpdate(field, newValue);
		}
	};

	const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setNotes(e.target.value);
	};

	const handleAccept = () => {
		onStatusChange(suggestion.id, "accepted");
	};

	const renderEvidenceLevel = () => {
		let color: string = "default";

		switch (suggestion.evidence_level) {
			case "guideline":
			case "meta_analysis":
				color = "green";
				break;
			case "RCT":
			case "cohort_study":
				color = "blue";
				break;
			case "case_series":
			case "expert_opinion":
				color = "orange";
				break;
		}

		return (
			<Tag color={color} style={{ fontSize: 9, padding: "0 4px", margin: 0, lineHeight: "16px" }}>
				{suggestion.evidence_level.replace("_", " ")}
			</Tag>
		);
	};

	const renderActionButtons = () => {
		return (
			<Space size={4} style={{ marginTop: 6 }}>
				{hasMedicalHistoryRecordType && !suggestion.suggestionRecordCreated && (
					<Button size="small" type="primary" icon={<CheckOutlined style={{ fontSize: 10 }} />} onClick={() => onShowMedicalHistoryPreview(suggestion.id)} style={{ fontSize: 10, height: 22, padding: "0 6px" }}>
						Add History
					</Button>
				)}

				{suggestion.medication && !suggestion.suggestionRecordCreated && (
					<Button size="small" type="primary" icon={<MedicineBoxOutlined style={{ fontSize: 10 }} />} onClick={() => onAddMedication(suggestion.id)} style={{ fontSize: 10, height: 22, padding: "0 6px" }}>
						Add Med
					</Button>
				)}

				{suggestion.testing && !suggestion.suggestionRecordCreated && (
					<Button size="small" type="primary" icon={<ExperimentOutlined style={{ fontSize: 10 }} />} onClick={() => onAddTest(suggestion.id)} style={{ fontSize: 10, height: 22, padding: "0 6px" }}>
						Order Test
					</Button>
				)}
			</Space>
		);
	};

	return (
		<Card
			size="small"
			style={{
				background: colorMapping[suggestion.type],
				borderRadius: 6,
				transition: "all 0.3s ease",
				opacity: animateIn ? 1 : 0,
				transform: animateIn ? "translateY(0)" : "translateY(-20px)",
				boxShadow: isExpanded ? "0 2px 8px rgba(0,0,0,0.1)" : "0 1px 3px rgba(0,0,0,0.05)",
				padding: "6px",
			}}
			styles={{ body: { padding: 0 } }}
		>
			<div style={{ display: "flex", flexDirection: "column" }}>
				{/* Header */}
				<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
					<div style={{ flex: 1, paddingRight: 4 }}>
						<Text
							style={{
								fontSize: 11,
								lineHeight: 1.4,
								textDecoration: suggestion.status === "accepted" ? "line-through" : "none",
								color: suggestion.status === "accepted" ? "var(--text-color-secondary)" : undefined,
							}}
						>
							{suggestion.text}
						</Text>
					</div>
				</div>

				{/* Metadata Bar */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						marginTop: 4,
						paddingTop: 4,
						borderTop: "1px solid rgba(0, 0, 0, 0.06)",
					}}
				>
					<Space size={2}>
						<Badge
							count={number + 1}
							size="small"
							style={{
								backgroundColor: "#1890ff",
								fontSize: 9,
								height: 14,
								lineHeight: "14px",
								minWidth: 14,
							}}
						/>
						<Tooltip title={suggestion.type}>
							<div
								style={{
									width: 18,
									height: 18,
									borderRadius: "50%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									background: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.5)",
								}}
							>
								<IconComponent style={{ fontSize: 10 }} />
							</div>
						</Tooltip>

						<Text type="secondary" style={{ fontSize: 9 }}>
							{new Date(suggestion.timestamp).toLocaleTimeString([], {
								hour: "2-digit",
								minute: "2-digit",
							})}
						</Text>

						{hasMedicalHistoryRecordType && (
							<Tag color={suggestion.suggestionRecordCreated ? "success" : "warning"} style={{ fontSize: 8, padding: "0 3px", margin: 0, lineHeight: "14px" }}>
								Hist
							</Tag>
						)}

						{suggestion.medication && (
							<Tag color={suggestion.suggestionRecordCreated ? "success" : "warning"} style={{ fontSize: 8, padding: "0 3px", margin: 0, lineHeight: "14px" }}>
								Med
							</Tag>
						)}

						{suggestion.testing && (
							<Tag color={suggestion.suggestionRecordCreated ? "success" : "warning"} style={{ fontSize: 8, padding: "0 3px", margin: 0, lineHeight: "14px" }}>
								Test
							</Tag>
						)}

						{suggestion.urgency === "critical" && (
							<Tag color="error" style={{ fontSize: 8, padding: "0 3px", margin: 0, lineHeight: "14px" }}>
								CRIT
							</Tag>
						)}
					</Space>

					<Space size={2}>
						{suggestion.respondedBy === "ai" && (
							<Tooltip title="AI identified response">
								<Tag color="blue" style={{ fontSize: 8, padding: "0 3px", margin: 0, lineHeight: "14px" }}>
									AI
								</Tag>
							</Tooltip>
						)}
						{suggestion.respondedBy === "practitioner" && (
							<Tooltip title="Practitioner responded">
								<Tag color="green" style={{ fontSize: 8, padding: "0 3px", margin: 0, lineHeight: "14px" }}>
									PR
								</Tag>
							</Tooltip>
						)}

						<Tooltip title={isExpanded ? "Collapse" : "Expand"}>
							<Button type="text" size="small" icon={isExpanded ? <UpOutlined style={{ fontSize: 9 }} /> : <DownOutlined style={{ fontSize: 9 }} />} onClick={toggleExpand} style={{ padding: 0, height: 18, width: 18 }} />
						</Tooltip>
					</Space>
				</div>

				{/* Expanded Content */}
				<CSSTransition in={isExpanded} timeout={300} classNames="card-expand" unmountOnExit nodeRef={expandContentRef}>
					<div ref={expandContentRef} style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(0, 0, 0, 0.06)" }}>
						{/* Compact Stats */}
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "1fr 1fr 1fr",
								gap: 6,
								marginBottom: 8,
								padding: 6,
								background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.5)",
								borderRadius: 3,
							}}
						>
							<div>
								<Text type="secondary" style={{ fontSize: 9, display: "block" }}>
									Confidence
								</Text>
								<div style={{ fontSize: 11, fontWeight: 500 }}>{suggestion.confidence.toFixed(2)}</div>
							</div>

							<div>
								<Text type="secondary" style={{ fontSize: 9, display: "block" }}>
									Urgency
								</Text>
								<div>
									<Tag color={urgencySeverity[suggestion.urgency]} style={{ fontSize: 9, padding: "0 4px", margin: 0, lineHeight: "16px" }}>
										{suggestion.urgency}
									</Tag>
								</div>
							</div>

							<div>
								<Text type="secondary" style={{ fontSize: 9, display: "block" }}>
									Evidence
								</Text>
								<div>{renderEvidenceLevel()}</div>
							</div>
						</div>

						{/* Reasoning */}
						<div style={{ marginBottom: 8 }}>
							<Text type="secondary" style={{ fontSize: 9, display: "block", marginBottom: 2 }}>
								Reasoning
							</Text>
							<div style={{ fontSize: 10, lineHeight: 1.4 }}>{suggestion.why}</div>
						</div>

						{/* Response Text */}
						{suggestion.responseText && (
							<div
								style={{
									marginBottom: 8,
									padding: 6,
									background: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 0.7)",
									borderRadius: 3,
									border: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.1)" : "var(--border-color)"}`,
								}}
							>
								<Text type="secondary" style={{ fontSize: 9, display: "block", marginBottom: 2 }}>
									{suggestion.respondedBy === "ai" ? "Detected:" : "Response:"}
								</Text>
								<div style={{ fontSize: 10, lineHeight: 1.4 }}>{suggestion.responseText}</div>
							</div>
						)}

						{/* Mapping Fields */}
						{Object.keys(mappingValues).length > 0 && (
							<div style={{ marginBottom: 8 }}>
								<Text strong style={{ fontSize: 10, display: "block", marginBottom: 4 }}>
									Profile Fields:
								</Text>
								<Space direction="vertical" style={{ width: "100%" }} size={4}>
									{Object.entries(mappingValues).map(([field, value]) => (
										<div key={field} style={{ display: "flex", alignItems: "center", gap: 6 }}>
											<Text style={{ fontSize: 10, minWidth: 70 }}>{field}:</Text>
											{field.toLowerCase().includes("date") ? (
												<DatePicker size="small" value={value ? dayjs(value) : null} onChange={(date) => handleMappingChange(field, date ? date.toISOString() : "")} style={{ flex: 1, fontSize: 10 }} />
											) : (
												<Input size="small" value={value} onChange={(e) => handleMappingChange(field, e.target.value)} style={{ flex: 1, fontSize: 10 }} />
											)}
										</div>
									))}
								</Space>
							</div>
						)}

						{/* Notes */}
						{suggestion.status !== "accepted" && (
							<div style={{ marginBottom: 8 }}>
								<TextArea rows={2} value={notes} onChange={handleNoteChange} placeholder="Notes..." style={{ fontSize: 10 }} size="small" />
							</div>
						)}

						{/* Action Buttons */}
						{creationPending && renderActionButtons()}

						{/* Accept/Delete */}
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 4,
								marginTop: 6,
								paddingTop: 6,
								borderTop: "1px solid rgba(0, 0, 0, 0.06)",
							}}
						>
							<Tooltip title="Accept">
								<Button type="primary" size="small" icon={<CheckOutlined style={{ fontSize: 10 }} />} onClick={handleAccept} disabled={suggestion.status === "accepted"} style={{ height: 22, padding: "0 8px" }} />
							</Tooltip>

							<Tooltip title="Delete">
								<Button
									danger
									size="small"
									icon={<DeleteOutlined style={{ fontSize: 10 }} />}
									onClick={() => onStatusChange(suggestion.id, "deleted")}
									disabled={suggestion.status === "accepted"}
									style={{ height: 22, padding: "0 8px" }}
								/>
							</Tooltip>
						</div>
					</div>
				</CSSTransition>
			</div>
		</Card>
	);
};

export default SuggestionPopup;
